import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import './App.css';

// Components
import AuthView from './components/AuthView';
import DashboardView from './components/DashboardView';
import AccountSettingsView from './components/AccountSettingsView';
import Sidebar from './components/Sidebar';
import PodcastFeedView from './components/PodcastFeedView';
import PodcastFeedSettingsView from './components/PodcastFeedSettingsView';
import EpisodeView from './components/EpisodeView';
import ConfirmEmailView from './components/ConfirmEmailView';
import BillingSetupView from './components/BillingSetupView';
import SocialMediaManagerView from './components/SocialMediaManagerView';
import BillingDashboardView from './components/BillingDashboardView';
import FacebookPageSelector from './components/FacebookPageSelector';
import WelcomeView from './components/WelcomeView';
import ForgotPasswordView from './components/ForgotPasswordView';

// Utils
import { apiPost, apiGet } from './utils/api';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

function App() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasBilling, setHasBilling] = useState(null);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);
  const [hasFeeds, setHasFeeds] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkBilling = async (userId) => {
      try {
        const response = await apiPost('/get-payment-methods', { 
          userId: userId || sessionStorage.getItem('userId') || email 
        });

        const hasPaymentMethods = response.paymentMethods && response.paymentMethods.length > 0;
        
        let subscriptionExpired = false;
        
        if (response.subscriptions && response.subscriptions.length > 0) {
          const subscription = response.subscriptions[0];
          const now = new Date();
          
          if (subscription.cancel_at_period_end && 
              new Date(subscription.current_period_end * 1000) < now) {
            subscriptionExpired = true;
          }
          
          const validStatuses = ['active', 'trialing'];
          if (!validStatuses.includes(subscription.status)) {
            subscriptionExpired = true;
          }
        }
        
        setHasBilling(hasPaymentMethods && !subscriptionExpired);
        setSubscriptionExpired(subscriptionExpired);
      } catch (error) {
        console.error('Error fetching billing information:', error);
        setHasBilling(false);
      }
    };

    const checkHasFeeds = async (userId) => {
      try {
        console.log('🔍 Checking hasFeeds for userId:', userId);
        
        // Ensure we have a valid token before making the API call
        const token = sessionStorage.getItem('authToken');
        if (!token) {
          console.warn('⚠️ No auth token found, attempting to fetch...');
          const session = await fetchAuthSession({ forceRefresh: true });
          const idToken = session.tokens?.idToken?.toString();
          if (idToken) {
            sessionStorage.setItem('authToken', idToken);
            console.log('✅ Token fetched successfully');
          } else {
            console.error('❌ Could not fetch auth token');
            setHasFeeds(false);
            return false;
          }
        }
        
        const response = await apiGet('/getPodcastFeeds');
        const userFeeds = response.feeds || [];
        const hasFeedsResult = userFeeds.length > 0;
        
        console.log('📡 Checked feeds:', { 
          feedCount: userFeeds.length, 
          hasFeeds: hasFeedsResult 
        });
        
        setHasFeeds(hasFeedsResult);
        return hasFeedsResult;
      } catch (error) {
        console.error('❌ Error checking feeds:', error);
        
        // If it's a 401 error, try refreshing the token once
        if (error.message && error.message.includes('401')) {
          console.log('🔄 Got 401 error, attempting token refresh...');
          try {
            const session = await fetchAuthSession({ forceRefresh: true });
            const idToken = session.tokens?.idToken?.toString();
            if (idToken) {
              sessionStorage.setItem('authToken', idToken);
              console.log('✅ Token refreshed, retrying feeds check...');
              
              // Retry the API call once
              const response = await apiGet('/getPodcastFeeds');
              const userFeeds = response.feeds || [];
              const hasFeedsResult = userFeeds.length > 0;
              
              console.log('📡 Retry successful:', { 
                feedCount: userFeeds.length, 
                hasFeeds: hasFeedsResult 
              });
              
              setHasFeeds(hasFeedsResult);
              return hasFeedsResult;
            }
          } catch (retryError) {
            console.error('❌ Retry failed:', retryError);
          }
        }
        
        // If all else fails, set to false but don't block the user
        setHasFeeds(false);
        return false;
      }
    };

    const handleBillingSetupComplete = async () => {
      setHasBilling(true);
      
      const userId = sessionStorage.getItem('userId');
      if (userId) {
        await checkHasFeeds(userId);
      }
    };

    const handleFeedsUpdated = async () => {
    try {
      console.log('🔄 Feeds updated event received');
      
      // Step 1: Refresh the auth token to ensure it's valid
      console.log('🔑 Refreshing auth token...');
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString();
      
      if (idToken) {
        sessionStorage.setItem('authToken', idToken);
        console.log('✅ Auth token refreshed');
      } else {
        console.error('❌ No auth token available after refresh');
        return;
      }
      
      // Step 2: Add a small delay to ensure feed is fully saved
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Step 3: Check for feeds
      const userId = sessionStorage.getItem('userId');
      if (userId) {
        console.log('📡 Checking for feeds...');
        await checkHasFeeds(userId);
        console.log('✅ Feeds check complete');
      } else {
        console.error('❌ No userId found in sessionStorage');
      }
    } catch (error) {
      console.error('❌ Error in handleFeedsUpdated:', error);
      // Don't throw - let the user continue, they can refresh if needed
    }
  };

    const checkAuthentication = async () => {
      try {
        setIsLoading(true);
        const user = await getCurrentUser();
        
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        
        if (idToken) {
          sessionStorage.setItem('authToken', idToken);
          
          const payload = session.tokens?.idToken?.payload;
          if (payload?.sub) {
            sessionStorage.setItem('userId', payload.sub);
          }
          
          setIsAuthenticated(true);
          
          let userEmail = payload?.email;
          
          if (!userEmail && user.attributes) {
            userEmail = user.attributes.email;
          }
          
          if (!userEmail && user.username && user.username.includes('@')) {
            userEmail = user.username;
          }
          
          if (userEmail && userEmail.includes('@')) {
            setEmail(userEmail);
          } else {
            console.warn('Could not determine user email from:', { 
              tokenEmail: payload?.email, 
              userAttributes: user.attributes,
              username: user.username 
            });
            setEmail('');
          }
          
          await checkBilling(payload?.sub);
          await checkHasFeeds(payload?.sub);
        }
        else {
          setIsAuthenticated(false);
          setHasBilling(null);
          setHasFeeds(null);
        }
      } catch (error) {
        console.log('No user signed in:', error);
        setIsAuthenticated(false);
        setHasBilling(null);
        setHasFeeds(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthentication();
    
    window.addEventListener('billing-setup-complete', handleBillingSetupComplete);
    window.addEventListener('feeds-updated', handleFeedsUpdated);

    return () => {
      window.removeEventListener('billing-setup-complete', handleBillingSetupComplete);
      window.removeEventListener('feeds-updated', handleFeedsUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated && hasBilling === false) {
      navigate('/billing-setup');
    }
  }, [isAuthenticated, hasBilling, navigate]);

  const authProps = {
    email, setEmail, password, setPassword,
    isAuthenticated, setIsAuthenticated, isNewUser, setIsNewUser,
    message, setMessage
  };

  if (isLoading) return <div className="app-loading-screen" id="appLoadingScreen">Loading...</div>;

  return (
    <div className="app-container" id="appRoot">
      {isAuthenticated && <Sidebar setIsAuthenticated={setIsAuthenticated} />}

      <div className={isAuthenticated ? "app-content-authenticated" : "app-content-public"} id="appMainContent">
        <Routes>
          {!isAuthenticated ? (
            <>
              <Route path="/login" element={<div className="auth-container" id="loginView"><AuthView {...authProps} /></div>} />
              <Route path="/confirm-email" element={<div className="confirm-email-container" id="confirmEmailView"><ConfirmEmailView email={email} setIsAuthenticated={setIsAuthenticated} setHasBilling={setHasBilling} /></div>} />
              <Route path="*" element={<div className="auth-container" id="defaultAuthView"><AuthView {...authProps} /></div>} />
              <Route path="/forgot-password" element={<div className="auth-container" id="forgotPasswordView"><ForgotPasswordView /></div>} />
            </>
          ) : hasBilling === false ? (
            <>
              <Route path="/billing-setup" element={
                <div className="billing-setup-container" id="billingSetupView">
                  <Elements stripe={stripePromise}>
                    <BillingSetupView email={email} />
                  </Elements>
                </div>
              } />
              <Route path="*" element={<Navigate to="/billing-setup" />} />
            </>
          ) : hasBilling === true && hasFeeds === false ? (
            <>
              <Route path="/welcome" element={<WelcomeView />} />
              <Route path="*" element={<Navigate to="/welcome" />} />
            </>
          ) : hasBilling === true && hasFeeds === null ? (
            <>
              <Route path="*" element={<div className="app-loading-screen">Checking your account...</div>} />
            </>
          ) : subscriptionExpired ? (
            <>
              <Route path="/billing" element={<div className="billing-dashboard-container" id="billingDashboardView"><BillingDashboardView email={email} /></div>} />
              <Route path="*" element={<Navigate to="/billing" />} />
            </>
          ) : (
            <>
              <Route path="/welcome" element={<WelcomeView />} />
              <Route path="/dashboard" element={<div className="dashboard-container" id="dashboardView"><DashboardView email={email} setMessage={setMessage} /></div>} />
              <Route path="/account-settings" element={<div className="account-settings-container" id="accountSettingsView"><AccountSettingsView email={email} setMessage={setMessage} /></div>} />
              <Route path="/billing" element={<div className="billing-dashboard-container" id="billingDashboardView"><BillingDashboardView email={email} /></div>} />
              <Route path="/podcast/:feedId" element={<div className="podcast-feed-container" id="podcastFeedView"><PodcastFeedView /></div>} />
              <Route path="/podcast/:feedId/episode/:episodeId" element={<div className="episode-container" id="episodeView"><EpisodeView /></div>} />
              <Route path="/podcast/:feedId/settings" element={<div className="podcast-settings-container" id="podcastSettingsView"><PodcastFeedSettingsView /></div>} />
              <Route path="/social-media-manager" element={<div className="social-media-manager-container" id="socialMediaManagerView"><SocialMediaManagerView /></div>} />
              <Route path="/facebook-page-selector" element={<div className="facebook-page-selector-view"><FacebookPageSelector /></div>} />
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </>
          )}
        </Routes>
      </div>
    </div>
  );
}

export default App;